<script setup lang="ts">
import vPreloadImg from '@/ts/directives/v-preload-img.ts';
import model from '@/ts/model.ts';
import Close from './close.vue';
import Btn from './btn.vue';
import { getLangText } from '@/ts/utils/model-utils';
import type { Game, GameInfoValue } from '@/ts/types/game.ts';
import { nextTick, ref, watch, type DirectiveBinding } from 'vue';
import { toCurrency } from '@/ts/management/currency-management.ts';
import { startGame } from '@/ts/management/gameplay-management.ts';
import { loadFollowingItems, showGameListShowcase } from '@/ts/management/gl-showcase-management.ts';
import PointLeftIcon from '@/assets/icons/point-left-icon.svg?raw';
import HeartIcon from '@/assets/icons/heart-icon.svg?raw';
import HeartFilledIcon from '@/assets/icons/heart-filled-icon.svg?raw';
import { addGameToFav, isGameFav } from '@/ts/management/user-management.ts';

type GameInfoWithKey = [string, boolean | GameInfoValue | undefined];

const elemScrollContainer = ref<HTMLElement>();
const btnPrevDisabled = ref<boolean>(false);
const btnNextDisabled = ref<boolean>(false);

function getGameData(game: Game): GameInfoWithKey[]{
    return [
        ['REELS', game.reels], 
        ['ROWS', game.rows], 
        ['MINBET', toCurrency(game.minBet || 0)], 
        ['MAXBET', toCurrency(game.maxBet || 0, {decimals: Number(game.maxBet || 0) >= 10 ? 0 : 2})], 
        ['BONUS', interpretBoolean(game.bonus)], 
        ['FREESPINS', interpretBoolean(game.freeSpins)], 
        ['WINLINES', interpretBoolean(game.winLines)], 
        ['WAYS', interpretBoolean(game.ways)], 
        ['CLUSTERS', interpretBoolean(game.clusters)], 
        ['JACKPOT', interpretBoolean(game.jackpot)],
    ];
}

function interpretBoolean(value?: boolean | GameInfoValue | number): string | number | undefined{
    if(typeof value === 'boolean'){
        return (value ? getLangText('YesLabel') : getLangText('NoLabel')) || ''; 
    }

    if(!value) return undefined;

    return value;
}

function onStartGame(game: Game, forMoney: boolean){
    showGameListShowcase(false, true);
    startGame(game.gameId, { forMoney });
}

const vSwipeContainer = {
    mounted(element: HTMLElement){
        nextTick().then(() => {
            onScroll();
            setupBtnScrollOffset();

            const targetElem = element.querySelector(`[data-game-id="${model.glShowcase.selectedGameId}"]`);
            if(!targetElem) return;
    
            const left = targetElem.getBoundingClientRect().left;
    
            element.scrollTo({left, behavior: 'instant'});
        });

        element.addEventListener('scroll', onScroll, { passive: true });

        function onScroll(){
            const offset = 100;
            const scrollLeft = element.scrollLeft;

            btnPrevDisabled.value = scrollLeft < offset;
            btnNextDisabled.value = scrollLeft > element.scrollWidth - element.clientWidth - offset;
        }
    },
};

const vThumbnailItem = {
    mounted(element: HTMLElement, bindings: DirectiveBinding<'left' | 'right' | undefined>){
        const dir = bindings.value;
        if(!dir) return;

        let t = 0;
        const observer = new IntersectionObserver(entries => {
            clearTimeout(t);
            t = setTimeout(() => {
                for(const entry of entries){
                    if(!entry.isIntersecting) continue;
                    loadFollowingItems(dir);
                    observer.disconnect();
                }
            }, 100);
        });

        observer.observe(element);
    },
};

let btnScrollOffset = 0;

window.addEventListener('resize', setupBtnScrollOffset);

async function setupBtnScrollOffset(){
    await nextTick();
    btnScrollOffset = elemScrollContainer.value?.clientWidth || 0;
}

function onPrevItemClick(){
    elemScrollContainer.value?.scrollTo({
        left: elemScrollContainer.value.scrollLeft - btnScrollOffset,
        behavior: 'smooth',
    });
}

function onNextItemClick(){
    elemScrollContainer.value?.scrollTo({
        left: elemScrollContainer.value.scrollLeft + btnScrollOffset,
        behavior: 'smooth',
    });
}

model.on('Navigation', () => showGameListShowcase(false, true));
watch(() => model.activeModal, () => showGameListShowcase(false, true));
</script>

<template>
    <teleport to="body" defer>
        <template v-if="model.glShowcase.displayed">
            <div class="mobile-game-list-showcase" @click.prevent="showGameListShowcase(false)">
                <div class="btn btn-prev" v-html="PointLeftIcon" @click.prevent.stop="onPrevItemClick()" :class="{'disabled': btnPrevDisabled}"></div>
                <div class="container" @click.prevent.stop>
                    <close :location="'gl-showcase'" :on-close="() => showGameListShowcase(false)" />
                    <div class="container-inner scroll-snap-container" v-swipe-container ref="elemScrollContainer">
                        <template v-for="info in model.glShowcase.list" :key="info.key">
                            <div class="thumbnail scroll-snap-item" :data-game-id="info.key">
                                <template v-if="info.displayed">
                                    <div class="img-container" v-thumbnail-item="info.loadMore">
                                        <template v-if="model.userLoggedIn">
                                            <div 
                                                :class="{
                                                    'is-fav': isGameFav(info.game.gameId),
                                                }" 
                                                v-html="isGameFav(info.game.gameId) ? HeartFilledIcon : HeartIcon" 
                                                @click.prevent.stop="addGameToFav(info.game.gameId)"
                                                class="btn-fav"
                                            ></div>
                                        </template>
                                        <div
                                            v-preload-img="{
                                                expectedValue: info.game.imgUrl,
                                                errorValue: info.game.imgUrlError,
                                            }"
                                            class="img"
                                        ></div>
                                    </div>
                                    <div class="game-info">
                                        <div class="img-name">
                                            {{ info.game.providerName }}
                                        </div>
                                        <div class="btns">
                                            <template v-if="!model.userLoggedIn && info.game.hasPlayFun">
                                                <btn 
                                                    :skin="'secondary'" 
                                                    :loc="'gl-showcase'" 
                                                    @click.prevent.stop="onStartGame(info.game, false)"
                                                >{{ getLangText('PlayFunLabel') }}</btn>
                                            </template>
                                            <btn 
                                                :skin="'primary'" 
                                                :loc="'gl-showcase'" 
                                                @click.prevent.stop="onStartGame(info.game, true)"
                                            >{{ getLangText('PlayMoneyShortLabel') }}</btn>
                                        </div>
                                        <div class="game-data">
                                            <template v-for="data in getGameData(info.game)">
                                                <template v-if="(typeof data[1] !== 'undefined')">
                                                    <div class="info">
                                                        <div class="key">{{ getLangText(data[0]) }}</div>
                                                        <div class="val">{{ data[1] }}</div>
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </div>
                </div>
                <div class="btn btn-next" v-html="PointLeftIcon" @click.prevent.stop="onNextItemClick()" :class="{'disabled': btnNextDisabled}"></div>
            </div>
        </template>
    </teleport>
</template>

<style lang="scss" scoped>
    @use '@/scss/shared';
    @use 'sass:color';
    @use 'sass:math';
    .mobile-game-list-showcase{
        position: fixed;
        display: flex;
        top:0;
        left:0;
        z-index: 280;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
        @include shared.site() using($site-key){
            background-color: color.adjust(shared.theme-color('neutral_10', $site-key), $alpha: -.25);
        }
        & > .btn{
            $dim: 40px;
            width: $dim;
            height: $dim;
            border-radius: 100%;
            margin: 5px;
            transition: opacity .25s;
            &.disabled{
                opacity: .5;
            }
            @include shared.site() using($site-key){
                background-color: shared.theme-color('comp3_4', $site-key);
            }
            &,
            & > *{
                display: flex;
                justify-content: center;
                align-items: center;
            }
            & > *{
                width: 30px;
                height: 30px;
            }
            &.btn-next{
                transform: rotate(180deg);
            }
        }
        & > .container{
            position: relative;
            animation-fill-mode: forwards;
            animation-duration: .5s;
            animation-name: gl-showcase-container-display;
            @keyframes gl-showcase-container-display {
                from{
                    opacity: .05;
                    transform: translateY(10px);
                }
                to{
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            & > .container-inner{
                $dim-width: 250px;
                $dim-height: 450px;
                $dim-width-landscape: $dim-height + 20px;
                $dim-height-landscape: $dim-width - 60px;
                display: flex;
                position: relative;
                width: $dim-width;
                height: $dim-height;
                background-color: #fff;
                overflow-x: scroll;
                @include shared.site() using($site-key){
                    color: shared.theme-color('primary_3', $site-key);
                }
                @media (orientation: landscape){
                    width: $dim-width-landscape;
                    height: $dim-height-landscape;
                }
                & > .thumbnail{
                    position: relative;
                    flex: 0 0 $dim-width;
                    width: 100%;
                    @media (orientation: landscape){
                        flex: 0 0 $dim-width-landscape;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                    }
                    & > .img-container{
                        position: relative;
                        width: calc(100% - 10px);
                        height: 210px;
                        margin: 5px;
                        @include shared.site() using($site-key){
                            background-color: color.adjust(shared.theme-color('primary_5', $site-key), $alpha: -.25);
                        }
                        @media (orientation: landscape){
                            width: math.div($dim-width-landscape, 2.5);
                            height: calc(100% - 10px);
                        }
                        & > .img{
                            width: 100%;
                            height: 100%;
                            background-position: center;
                            background-repeat: no-repeat;
                            background-size: cover;
                            animation-fill-mode: forwards;
                            animation-duration: 1s;
                            animation-name: img-display;
                            @keyframes img-display {
                                from{
                                    opacity: 0;
                                }
                                to{
                                    opacity: 1;
                                }
                            }
                        }
                        & > .btn-fav{
                            position: absolute;
                            top:0;
                            left:0;
                            z-index: 1;
                            color: #fff;
                            padding: 5px;
                            border-radius: 100%;
                            &,
                            & > *{
                                display: flex;
                                justify-content: center;
                                align-items: center;
                            }
                            & > *{
                                filter: drop-shadow(2px 2px 0 #0000007c);
                            }
                        }
                    }
                    & > .game-info{
                        position: relative;
                        & > .img-name{
                            text-align: center;
                            font-size: 18px;
                            line-height: 18px;
                            padding: 10px 0;
                            @include shared.casino-section-title();
                        }
                        & > .btns{
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            margin-top: 10px;
                            padding: 0;
                        }
                        @media (orientation: landscape){
                            flex: 1;
                        }
                        & > .game-data{
                            display: grid;
                            grid-template-columns: repeat(2, 1fr);
                            padding: 10px;
                            column-gap: 10px;
                            & > .info{
                                display: flex;
                                justify-content: flex-start;
                                align-items: center;
                                border-bottom: 1px solid #000;
                                font-weight: 500;
                                font-size: 14px;
                                text-transform: uppercase;
                                & > *{
                                    flex: auto;
                                }
                                & > .key{
                                    color: #000;
                                }
                                & > .val{
                                    text-align: right;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
</style>